import { useCallback, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { runInBackground } from '../../../../configuration/setup/backgroundActions';
import { trace } from '../../../../configuration/setup/trace';
import { FileExport } from '../common.types';

export type ActiveDownload = {
    unwrap(): Promise<FileExport>;
    abort(): void;
};

export type UseDownloadLifecycleProps = {
    initDownload: () => ActiveDownload;
    onError: (error: unknown) => void;
    onDownloadFinished: () => void;
    fallbackFilename: string;
};

/**
 * A custom hook to start and potentially cancel a download.
 *
 * Known behavior:
 *
 * The error handling and downloading code runs concurrent and
 * delayed to the rendering. Therefore, the calling code could
 * trigger a re-render with changed onDownloadFinished or onError
 * callbacks. The background action would then still use the old
 * ones when the download finishes.
 *
 * This hook assumes the client code wants to execute the callbacks
 * as they were at the time the startDownload function was called.
 *
 * This is an issue if the callbacks use some state (e.g. from
 * useState) to make a decision. In this case the client code
 * should read the latest state from a mutable reference (obtained
 * by useRef).
 *
 * This is no issue for callbacks that use a setter from useState,
 * because those functions are {@link https://react.dev/reference/react/useState#setstate|stable}
 * (e.g. useState always returns the same function).
 *
 * @param initDownload A function returning a RTK query mutation action result that represents an ongoing download
 * @param onError A function that is called if an error occurred during the download.
 *                This function is not called for cancelled downloads.
 * @param onDownloadFinished A function that is called after the download finished successfully.
 * @param fallbackFilename A filename used if the backend didn't send any filename information.
 */
export const useDownloadLifecycle = ({
    initDownload,
    onError,
    onDownloadFinished,
    fallbackFilename,
}: UseDownloadLifecycleProps) => {
    const [ongoingDownload, setOngoingDownload] = useState<ActiveDownload | null>(null);

    const startDownload = useCallback(() => {
        if (ongoingDownload !== null) {
            return;
        }

        const download = initDownload();

        setOngoingDownload(download);

        runInBackground(async () => {
            try {
                performDownload(await download.unwrap(), fallbackFilename);
                onDownloadFinished();
            } catch (error: unknown) {
                if (isAbortError(error)) {
                    trace('Download was aborted');
                } else {
                    onError(error);
                }
            } finally {
                setOngoingDownload(null);
            }
        });
    }, [ongoingDownload, setOngoingDownload, initDownload, onError, onDownloadFinished, fallbackFilename]);

    const cancelDownload = useCallback(() => {
        ongoingDownload?.abort();
    }, [ongoingDownload]);

    return {
        downloadIsActive: ongoingDownload !== null,
        startDownload,
        cancelDownload,
    };
};

const isAbortError = (error: unknown): error is SerializedError & { name: 'AbortError' } => {
    return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
};

const performDownload = ({ fileLink, fileName }: FileExport, fallbackFilename: string) => {
    const link = document.createElement('a');

    link.href = fileLink;
    link.download = fileName ?? fallbackFilename;
    link.target = '_blank';
    link.className = 'display-none';
    link.setAttribute('aria-hidden', 'true');

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
        window.URL.revokeObjectURL(fileLink);
    }, 40000);
};
